import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

/**
 * CabeceraPagos
 * Reutilizable para /reserva y /checkout
 *
 * Props:
 * - paso: number (1..totalPasos)
 * - totalPasos?: number (default 2)
 * - labels?: { paso1?: string, paso2?: string }
 * - segundosRestantes?: number | null (si null, no muestra countdown)
 * - duracionSegundos?: number (default 1800 = 30 min)
 * - onCancelar?: () => void
 * - idComprador?: string (requerido para cancelar internamente)
 * - onCancelSuccess?: () => void (callback para que la pantalla recargue datos)
 */
export default function CabeceraPagos({ 
  paso,
  totalPasos = 2,
  labels = { paso1: "Seleccionar fecha", paso2: "Pagar" },
  segundosRestantes = null,
  duracionSegundos = 30 * 60,
  onCancelar,
  idComprador,
  onCancelSuccess,
}) {

  const [segundosMostrados, setSegundosMostrados] = useState(
  typeof segundosRestantes === "number" ? segundosRestantes : null
);

useEffect(() => {
  if (typeof segundosRestantes !== "number") {
    setSegundosMostrados(null);
    return;
  }

  setSegundosMostrados(Math.max(0, Math.floor(segundosRestantes)));
}, [segundosRestantes]);

useEffect(() => {
  if (typeof segundosMostrados !== "number" || segundosMostrados <= 0) return;

  const id = setInterval(() => {
    setSegundosMostrados((actual) => {
      if (typeof actual !== "number") return null;
      return Math.max(0, actual - 1);
    });
  }, 1000);

  return () => clearInterval(id);
}, [segundosMostrados]);

  
  const tiempoTexto = useMemo(() => {
    if (segundosMostrados === null || typeof segundosMostrados !== "number") return null;
    const s = Math.max(0, Math.floor(segundosMostrados));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  }, [segundosMostrados]);

  const progresoCheckout = useMemo(() => {
    if (segundosMostrados === null || typeof segundosMostrados !== "number") return null;
    const total = Math.max(1, duracionSegundos);
    const restante = Math.max(0, Math.min(total, Math.floor(segundosMostrados)));
    const usado = Math.min(total, Math.max(0, total - restante));
    return Math.round((usado / total) * 100);
  }, [segundosMostrados, duracionSegundos]);

  const pasoSeguro = Math.min(Math.max(Number(paso) || 1, 1), totalPasos);
  
  const progresoPasos = useMemo(() => {
    if (totalPasos <= 1) return 100;
    return Math.round(((pasoSeguro - 1) / (totalPasos - 1)) * 100);
  }, [pasoSeguro, totalPasos]);

  const [cancelando, setCancelando] = useState(false);

  async function cancelarReservaInterno() {
    if (cancelando) return;

    if (!idComprador || idComprador === "NA") {
      return Swal.fire({
        icon: "error",
        title: "No disponible",
        text: "No encontramos tu sesión para cancelar la reserva.",
        customClass: { popup: "mi-modal", confirmButton: "mi-boton-confirmar" },
      });
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Cancelar reserva",
      text: "¿Seguro que deseas cancelar esta reserva? Se eliminará del carrito.",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
      customClass: {
        popup: "mi-modal",
        confirmButton: "mi-boton-confirmar",
        cancelButton: "mi-boton-cancelar",
      },
    });

    if (!result.isConfirmed) return;

    try {
      setCancelando(true);

      await axios.post("/cancelar-reserva", {
        params: { idComprador },
      });

      await Swal.fire({
        icon: "success",
        title: "Reserva cancelada",
        text: "Tu reserva fue cancelada correctamente.",
        confirmButtonText: "Continuar",
        customClass: { popup: "mi-modal", confirmButton: "mi-boton-confirmar" },
      });

      // Preferido: que la pantalla recargue datos y actualice estados
      if (typeof onCancelSuccess === "function") {
        onCancelSuccess();
        return;
      }

      // Fallback: recargar página
      window.location.reload();
    } catch (err) {
      console.log("Error cancelando reserva:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No pudimos cancelar la reserva. Intenta de nuevo.",
        customClass: { popup: "mi-modal", confirmButton: "mi-boton-confirmar" },
      });
    } finally {
      setCancelando(false);
    }
  }
  
  return (
    <div className="mb-3">
      <div className="row g-3 align-items-stretch">
        {/* Columna 1: Timeline */}
        <div className={segundosMostrados !== null ? "col-12 col-lg-7" : "col-12"}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <span
                    className={`badge rounded-pill ${pasoSeguro >= 1 ? "bg-primary" : "bg-secondary"}`}
                  >
                    1
                  </span>
                  <div className={`${pasoSeguro === 1 ? "fw-semibold" : "text-body-secondary"}`}>
                    {labels?.paso1 || "Seleccionar fecha"}
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span
                    className={`badge rounded-pill ${pasoSeguro >= 2 ? "bg-primary" : "bg-secondary"}`}
                  >
                    2
                  </span>
                  <div className={`${pasoSeguro === 2 ? "fw-semibold" : "text-body-secondary"}`}>
                    {labels?.paso2 || "Pagar"}
                  </div>
                </div>
              </div>

              <div className="mt-2" style={{ position: "relative" }}>
                <div
                  style={{
                    height: 6,
                    borderRadius: 999,
                    background: "var(--color-track-bg)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${progresoPasos}%`,
                      borderRadius: 999,
                    }}
                    className="bg-primary"
                  />
                </div>

                <div className="d-flex justify-content-between mt-1" style={{ fontSize: 12 }}>
                  <span className="text-body-secondary">
                    Paso {pasoSeguro} de {totalPasos}
                  </span>
                  <span className="text-body-secondary">Progreso {progresoPasos}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Columna 2: Tiempo de finalización */}
        {segundosMostrados !== null ? (
          <div className="col-12 col-lg-5">
            <div className={`card border-0 shadow-sm h-100 ${segundosMostrados === 0 ? "opacity-75" : ""}`}>
              <div className="card-body py-3 d-flex flex-column">
                <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                  <div>
                    <div className="fw-semibold">Tiempo para finalizar</div>
                    <div className="text-body-secondary" style={{ fontSize: 13 }}>
                      Restante: <span className="fw-semibold">{tiempoTexto}</span>
                    </div>
                  </div>

                  {(typeof onCancelar === "function" || typeof idComprador === "string") ? (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={typeof onCancelar === "function" ? onCancelar : cancelarReservaInterno}
                      disabled={segundosMostrados === 0 || cancelando}
                    >
                      {cancelando ? "Cancelando..." : "Cancelar"}
                    </button>
                  ) : null}
                </div>

                <div className="text-body-secondary mt-1" style={{ fontSize: 13 }}>
                  {segundosMostrados === 0 ? "La reserva expiró" : `Tienes ${Math.round(duracionSegundos / 60)} min para pagar`}
                </div>

                <div className="progress mt-2" style={{ height: 8 }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${progresoCheckout || 0}%` }}
                    aria-valuenow={progresoCheckout || 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>

                {segundosMostrados === 0 ? (
                  <div className="mt-2 text-danger" style={{ fontSize: 13 }}>
                    Tu reserva expiró.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}